/* globe-hero.js (full) */
(() => {
  const CDN_GLOBE = "https://cdn.jsdelivr.net/npm/globe.gl";

  function loadScriptOnce(src, globalCheck) {
    return new Promise((resolve, reject) => {
      if (globalCheck && globalCheck()) return resolve();

      const existing = document.querySelector(`script[data-loaded-src="${src}"]`);
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error(`Failed to load ${src}`)));
        return;
      }

      const s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.dataset.loadedSrc = src;
      s.onload = () => resolve();
      s.onerror = () => reject(new Error(`Failed to load ${src}`));
      document.head.appendChild(s);
    });
  }

  function normalize(str) {
    return (str || "").toString().trim().toLowerCase();
  }

  function safeJsonParse(text) {
    try { return JSON.parse(text); } catch { return null; }
  }

  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.__globeHeroInit) return;
    sectionEl.__globeHeroInit = true;

    const configTag = sectionEl.querySelector("[data-globe-hero-config]");
    if (!configTag) return;

    const config = safeJsonParse(configTag.textContent);
    if (!config) return;

    const canvasEl = sectionEl.querySelector(".globe-hero__canvas");
    const searchInput = sectionEl.querySelector(".globe-hero__search-input");
    const searchResults = sectionEl.querySelector(".globe-hero__search-results");

    const panelEmpty = sectionEl.querySelector(".globe-hero__panel-empty");
    const panelContent = sectionEl.querySelector(".globe-hero__panel-content");
    const panelTitle = sectionEl.querySelector(".globe-hero__panel-title");
    const panelMeta = sectionEl.querySelector(".globe-hero__panel-meta");
    const panelBlurb = sectionEl.querySelector(".globe-hero__panel-blurb");
    const panelImage = sectionEl.querySelector(".globe-hero__panel-image");
    const panelImageLink = sectionEl.querySelector(".globe-hero__panel-image-link");
    const panelCta = sectionEl.querySelector(".globe-hero__panel-cta");
    const panelSave = sectionEl.querySelector(".globe-hero__panel-save");

    const hintEl = sectionEl.querySelector("[data-globe-hint]");
    const zoomBtns = sectionEl.querySelectorAll("[data-zoom]");

    const allLocations = Array.isArray(config.locations) ? config.locations : [];
    const byId = new Map(allLocations.map((l) => [l.id, l]));

    // Persistence keys
    const hintKey = `globe_hint_dismissed_${config.sectionId}`;
    const recentKey = `globe_recent_${config.sectionId}`;
    const placesKey = "globe_your_places";

    function readList(key, fallback = []) {
      try {
        const v = JSON.parse(localStorage.getItem(key) || "null");
        return Array.isArray(v) ? v : fallback;
      } catch {
        return fallback;
      }
    }

    function writeList(key, list, maxLen) {
      const trimmed = Array.isArray(list) ? list.slice(0, maxLen) : [];
      try { localStorage.setItem(key, JSON.stringify(trimmed)); } catch {}
    }

    let recent = readList(recentKey, []);
    let selectedId = null;

    function markRecent(id) {
      recent = [id, ...recent.filter((x) => x !== id)];
      writeList(recentKey, recent, 12);
    }

    // Lazy init when visible
    const io = new IntersectionObserver(async (entries) => {
      const entry = entries[0];
      if (!entry.isIntersecting) return;
      io.disconnect();

      try {
        await loadScriptOnce(CDN_GLOBE, () => typeof window.Globe === "function");
      } catch (err) {
        console.error(err);
        return;
      }

      if (typeof window.Globe !== "function") return;

      // Tooltip (micro-story)
      const tooltip = document.createElement("div");
      tooltip.className = "globe-hero__tooltip";
      tooltip.innerHTML = `
        <p class="globe-hero__tooltip-title"></p>
        <p class="globe-hero__tooltip-blurb"></p>
      `;
      document.body.appendChild(tooltip);

      const tooltipTitle = tooltip.querySelector(".globe-hero__tooltip-title");
      const tooltipBlurb = tooltip.querySelector(".globe-hero__tooltip-blurb");

      let mouse = { x: 0, y: 0 };
      const onMouseMove = (e) => {
        mouse = { x: e.clientX, y: e.clientY };
        if (tooltip.classList.contains("is-visible")) {
          tooltip.style.left = `${mouse.x}px`;
          tooltip.style.top = `${mouse.y}px`;
        }
      };
      window.addEventListener("mousemove", onMouseMove, { passive: true });

      function showTooltip(loc) {
        if (!loc) return;
        const title = [loc.city, loc.region].filter(Boolean).join(", ");
        tooltipTitle.textContent = title;
        const b = (loc.blurb || "").toString().trim();
        tooltipBlurb.textContent = b;
        tooltip.style.left = `${mouse.x}px`;
        tooltip.style.top = `${mouse.y}px`;
        tooltip.classList.add("is-visible");
      }

      function hideTooltip() {
        tooltip.classList.remove("is-visible");
      }

      // Create globe
      const globe = new window.Globe(canvasEl, {
        rendererConfig: { antialias: true, alpha: true },
        waitForGlobeReady: true,
        animateIn: true
      });

      sectionEl.__globeInstance = globe;
      sectionEl.__tooltipEl = tooltip;
      sectionEl.__onMouseMove = onMouseMove;

      // Globe basics
      if (config.textureUrl) globe.globeImageUrl(config.textureUrl);
      globe.backgroundColor("rgba(0,0,0,0)");

      // Marker styling by tier, recent, selected
      globe
        .pointColor((d) => {
          const id = d && d.id;
          const tier = Number(d && d.tier ? d.tier : 1);

          if (id && selectedId && id === selectedId) return "rgba(255,255,255,0.95)";
          if (id && recent.includes(id)) return "rgba(255,255,255,0.55)";

          if (tier === 1) return "rgba(255,255,255,0.45)";
          if (tier === 2) return "rgba(255,255,255,0.25)";
          return "rgba(255,255,255,0.12)";
        })
        .pointsData(allLocations)
        .pointLat("lat")
        .pointLng("lng")
        .pointRadius((d) => {
          const base = (Number(config.pointRadius || 2) * 0.12);
          const tier = Number(d && d.tier ? d.tier : 1);
          if (tier === 1) return base * 1.25;
          if (tier === 2) return base * 1.0;
          return base * 0.85;
        })
        .pointAltitude((d) => {
          const base = (Number(config.pointAltitude || 8) * 0.002);
          const tier = Number(d && d.tier ? d.tier : 1);
          if (tier === 1) return base * 1.2;
          if (tier === 2) return base * 1.0;
          return base * 0.9;
        })
        .pointsMerge(false)
        .onPointHover((point) => {
          if (!point || !point.id) return hideTooltip();
          showTooltip(point);
        })
        .onPointClick((point) => {
          if (!point || !point.id) return;
          selectLocation(point.id, true);
        });

      // Optional boundaries
      if (config.boundariesGeojsonUrl) {
        fetch(config.boundariesGeojsonUrl)
          .then((r) => r.json())
          .then((geo) => {
            const feats = geo && geo.features ? geo.features : [];
            if (!feats.length) return;

            globe
              .polygonsData(feats)
              .polygonGeoJsonGeometry("geometry")
              .polygonAltitude(0.003)
              .polygonCapColor(() => "rgba(255,255,255,0)")
              .polygonSideColor(() => "rgba(255,255,255,0)")
              .polygonStrokeColor(() => "rgba(255,255,255,0.25)")
              .polygonsTransitionDuration(0);
          })
          .catch(() => {});
      }

const controls = typeof globe.controls === "function" ? globe.controls() : null;
const cam = typeof globe.camera === "function" ? globe.camera() : null;

if (controls && cam) {
  controls.enablePan = false;

  // ✅ Disable scroll-wheel / pinch zoom. Buttons still work via zoomBy()
  controls.enableZoom = false;

  // keep your limits (still useful for button zoom clamping)
  controls.minDistance = Number(config.minDistance || 140);
  controls.maxDistance = Number(config.maxDistance || 320);
}


      // Guided hint
      if (hintEl) {
        const dismissed = localStorage.getItem(hintKey) === "1";
        if (dismissed) hintEl.classList.add("is-hidden");
      }

      function dismissHint() {
        if (!hintEl) return;
        hintEl.classList.add("is-hidden");
        try { localStorage.setItem(hintKey, "1"); } catch {}
      }

      if (controls) controls.addEventListener("start", dismissHint);
      sectionEl.addEventListener("wheel", dismissHint, { passive: true });
      sectionEl.addEventListener("touchstart", dismissHint, { passive: true });

      // Initial view
      if (typeof globe.pointOfView === "function") {
        globe.pointOfView({ lat: 20, lng: 0, altitude: Number(config.initialAltitude || 2) }, 0);
      }

      function zoomBy(factor) {
        if (!controls || !cam) return;
        const minD = Number(config.minDistance || controls.minDistance || 140);
        const maxD = Number(config.maxDistance || controls.maxDistance || 320);
        const current = cam.position.length();
        const next = Math.max(minD, Math.min(maxD, current * factor));
        cam.position.setLength(next);
        controls.update();
      }

      zoomBtns.forEach((btn) => {
        btn.addEventListener("click", () => {
          const dir = btn.getAttribute("data-zoom");
          if (dir === "in") zoomBy(0.85);
          if (dir === "out") zoomBy(1.15);
        });
      });

      // Tiered visibility based on zoom distance
      function getCamDistance() {
        const c = typeof globe.camera === "function" ? globe.camera() : null;
        return c ? c.position.length() : 9999;
      }

      function visibleTiersForDistance(d) {
        const far = Number(config.maxDistance || 320);
        const near = Number(config.minDistance || 140);
        const denom = Math.max(1, (far - near));
        const t = Math.max(0, Math.min(1, (d - near) / denom)); // 0 close, 1 far

        if (t > 0.65) return new Set([1]);
        if (t > 0.30) return new Set([1, 2]);
        return new Set([1, 2, 3]);
      }

      let activeTiers = new Set([1, 2, 3]);

      function updateVisiblePoints() {
        const d = getCamDistance();
        const tiers = visibleTiersForDistance(d);

        const same =
          tiers.size === activeTiers.size &&
          [...tiers].every((x) => activeTiers.has(x));

        if (same) return;

        activeTiers = tiers;
        const filtered = allLocations.filter((l) => tiers.has(Number(l.tier || 1)));
        globe.pointsData(filtered);
      }

      let zoomRaf = null;
      function onZoomChange() {
        if (zoomRaf) return;
        zoomRaf = requestAnimationFrame(() => {
          zoomRaf = null;
          updateVisiblePoints();
        });
      }

      if (controls) controls.addEventListener("change", onZoomChange);
      updateVisiblePoints();

      function renderPanel(loc) {
        const title = [loc.city, loc.region].filter(Boolean).join(", ");
        const lat = Number(loc.lat).toFixed(4);
        const lng = Number(loc.lng).toFixed(4);

        panelTitle.textContent = title || "Selected location";
        panelMeta.textContent = `Lat ${lat} · Lng ${lng}`;

        if (panelBlurb) {
          const b = (loc.blurb || "").toString().trim();
          panelBlurb.textContent = b;
          panelBlurb.hidden = !b;
        }

        const col = loc.collection || {};
        const img = col.image || "";

        panelImage.src = img;
        panelImage.alt = col.title ? col.title : title;

        panelImageLink.href = col.url || "#";
        panelCta.href = col.url || "#";
        panelCta.textContent = "View collection";

        panelEmpty.hidden = true;
        panelContent.hidden = false;
      }

      function flyTo(loc) {
        if (typeof globe.pointOfView !== "function") return;
        globe.pointOfView(
          { lat: loc.lat, lng: loc.lng, altitude: Number(config.initialAltitude || 2) },
          900
        );
      }

      function refreshPointColors() {
        // Nudge Globe.gl to re-evaluate accessors
        const current = globe.pointsData();
        globe.pointsData(current);
      }

      function selectLocation(id, animate) {
        const loc = byId.get(id);
        if (!loc) return;

        selectedId = id;
        markRecent(id);

        renderPanel(loc);
        refreshPointColors();

        if (animate) flyTo(loc);
        hideResults();
      }

      function hideResults() {
        if (!searchResults) return;
        searchResults.innerHTML = "";
        searchResults.hidden = true;
      }

      function showResults(items) {
        if (!searchResults) return;
        if (!items.length) return hideResults();

        searchResults.innerHTML = items.map((item) => {
          const label = [item.city, item.region].filter(Boolean).join(", ");
          return `<button type="button" data-id="${item.id}">${label}</button>`;
        }).join("");

        searchResults.hidden = false;

        searchResults.querySelectorAll("button").forEach((btn) => {
          btn.addEventListener("click", () => {
            const id = btn.getAttribute("data-id");
            selectLocation(id, true);
          });
        });
      }

      if (searchInput) {
        searchInput.addEventListener("input", () => {
          const q = normalize(searchInput.value);
          if (!q) return hideResults();

          const matches = allLocations
            .map((l) => ({ l, label: normalize(`${l.city} ${l.region}`) }))
            .filter((x) => x.label.includes(q))
            .slice(0, 8)
            .map((x) => x.l);

          showResults(matches);

          // Premium touch: if exact match, focus it
          const exact = allLocations.find((l) => normalize(l.city) === q);
          if (exact) selectLocation(exact.id, true);
        });

        document.addEventListener("click", (e) => {
          if (!sectionEl.contains(e.target)) hideResults();
        });
      }

      // Your Places button
      if (panelSave) {
        panelSave.addEventListener("click", () => {
          if (!selectedId) return;
          const loc = byId.get(selectedId);
          if (!loc) return;

          const places = readList(placesKey, []);
          const payload = {
            id: loc.id,
            city: loc.city,
            region: loc.region,
            url: loc.collection && loc.collection.url ? loc.collection.url : "#"
          };

          const next = [payload, ...places.filter((p) => p && p.id !== loc.id)];
          writeList(placesKey, next, 30);

          const prev = panelSave.textContent;
          panelSave.textContent = "Added";
          setTimeout(() => { panelSave.textContent = prev; }, 1000);
        });
      }

      // Expose for internal use
      sectionEl.__selectLocation = selectLocation;

    }, { rootMargin: "200px" });

    io.observe(sectionEl);
    sectionEl.__globeObserver = io;
  }

  function initAll() {
    document.querySelectorAll("[data-globe-hero]").forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (e) => {
    initSection(e.target);
  });

  document.addEventListener("shopify:section:unload", (e) => {
    const el = e.target;
    if (el && el.__globeObserver) el.__globeObserver.disconnect();

    if (el && el.__tooltipEl) el.__tooltipEl.remove();
    if (el && el.__onMouseMove) window.removeEventListener("mousemove", el.__onMouseMove);

    if (el) {
      el.__globeHeroInit = false;
      el.__globeInstance = null;
      el.__selectLocation = null;
      el.__tooltipEl = null;
      el.__onMouseMove = null;
    }
  });
})();
